import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Address from "_shared/addresses/address";
import AddressSchema from "_shared/addresses/address-schema";
import Guid from "_shared/guids/guid";
import Name from "_shared/names/name";
import TitleSchema from "_shared/titles/_schema/title-schema";
import Title from "_shared/titles/title";
import EntityNameSchema from "legal-entities/entity-names/_schema/entity-name-schema";
import EntityName from "legal-entities/entity-names/entity-name";
import InvitationMessageSchema from "legal-entities/invitations/messages/_schema/invitation-message-schema";
import InvitationMessage from "legal-entities/invitations/messages/invitation-message";
import { InvitationStatus } from "legal-entities/invitations/statuses/invitation-status";
import RegistrationInfoSchema from "legal-entities/registration-info/_schema/registration-info-schema";
import RegistrationInfo from "legal-entities/registration-info/registration-info";
import { z } from "zod";

export type EntityOfficerInvitationJSON = z.infer<typeof EntityOfficerInvitationSchema.EntityOfficerInvitationJSONZ>;
export type EntityOfficerInvitationParams = z.infer<typeof EntityOfficerInvitationSchema.EntityOfficerInvitationZ>;

export default class EntityOfficerInvitationSchema {
  public static readonly EntityOfficerInvitationJSONZ = z.object({
    id: z.uuid().nullish(),
    entityName: EntityNameSchema.EntityNameJSONZ,
    entityLocation: AddressSchema.AddressJSONZ,
    email: z.email(),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    title: z.string().min(1),
    message: InvitationMessageSchema.InvitationMessageJSONZ.nullish(),
    userId: z.uuid().nullish(),
    status: z.enum(InvitationStatus).nullish(),
  });

  public static readonly EntityOfficerInvitationZ = z.object({
    id: z.instanceof(Guid).optional(),
    entityName: z.instanceof(EntityName),
    entityLocation: z.instanceof(Address),
    title: z.instanceof(Title),
    registrationInfo: z.instanceof(RegistrationInfo),
    message: z.instanceof(InvitationMessage).optional(),
    userId: z.instanceof(Guid).optional(),
    status: z.enum(InvitationStatus).optional(),
  });

  public static validate(data: unknown, logError: boolean = true): EntityOfficerInvitationParams {
    return parseOrThrow(EntityOfficerInvitationSchema.EntityOfficerInvitationZ, data, {
      logError,
      schemaName: "EntityOfficerInvitationSchema",
      operation: "validate",
      payloadSample: data,
    });
  }

  public static validateJSON(data: unknown): EntityOfficerInvitationJSON {
    return parseOrThrow(EntityOfficerInvitationSchema.EntityOfficerInvitationJSONZ, data, {
      logError: true,
      schemaName: "EntityOfficerInvitationSchema",
      operation: "validateJSON",
      payloadSample: data,
    });
  }

  public static isValid(data: unknown): data is EntityOfficerInvitationParams {
    try {
      EntityOfficerInvitationSchema.validate(data);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(data: unknown): data is EntityOfficerInvitationJSON {
    try {
      EntityOfficerInvitationSchema.validateJSON(data);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: EntityOfficerInvitationJSON): EntityOfficerInvitationParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(EntityOfficerInvitationSchema.EntityOfficerInvitationJSONZ, json, {
      logError: true,
      schemaName: "EntityOfficerInvitationSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return EntityOfficerInvitationSchema.codec.decode(parsed);
  }

  public static serialize(data: EntityOfficerInvitationParams): EntityOfficerInvitationJSON {
    if (isNullOrUndefined(data)) return data;
    const parsed = EntityOfficerInvitationSchema.validate(data);
    return EntityOfficerInvitationSchema.codec.encode(parsed);
  }

  public static readonly codec = z.codec(
    EntityOfficerInvitationSchema.EntityOfficerInvitationJSONZ,
    EntityOfficerInvitationSchema.EntityOfficerInvitationZ,
    {
      decode: (data) => ({
        id: Guid.fromJSON(data.id)!,
        entityName: new EntityName(EntityNameSchema.deserialize(data.entityName)),
        entityLocation: new Address(AddressSchema.deserialize(data.entityLocation)),
        title: new Title(TitleSchema.deserialize(data.title)),
        registrationInfo: new RegistrationInfo(
          RegistrationInfoSchema.deserialize({
            email: data.email,
            name: Name.create({
              params: {
                firstName: data.firstName,
                lastName: data.lastName,
              },
            }),
          }),
        ),
        message: InvitationMessage.fromJSON(data.message) ?? undefined,
        userId: Guid.fromJSON(data.userId) ?? undefined,
        status: data.status ?? undefined,
      }),
      encode: (invitation) => ({
        id: invitation.id ? invitation.id.value : undefined,
        entityName: invitation.entityName.toJSON(),
        entityLocation: invitation.entityLocation.toJSON(),
        email: invitation.registrationInfo.email.value,
        firstName: invitation.registrationInfo.name.firstName ?? "undefined",
        lastName: invitation.registrationInfo.name.lastName ?? "undefined",
        title: invitation.title.toJSON(),
        message: invitation.message?.toJSON(),
        userId: invitation.userId ? invitation.userId.toJSON() : undefined,
        status: invitation.status,
      }),
    },
  );
}
